import { useState, useEffect, useCallback, useMemo } from "react";
import { Star, ThumbsUp, Camera, CheckCircle2, MessageSquare, Loader2, ArrowUpDown, ImageIcon, BadgeCheck } from "lucide-react";
import { getSupabase } from "@/integrations/supabase/lazy";
import { WriteReviewForm } from "@/components/pdp/WriteReviewForm";
import { toast } from "sonner";


export interface Review {
  id: string;
  author: string;
  rating: number;
  title: string;
  body: string;
  date: string;
  verified: boolean;
  photos: string[];
  country: string;
  source?: "lai" | "user";
  helpfulCount?: number;
}

type SortOption = "newest" | "oldest" | "highest" | "lowest" | "helpful";

interface ProductReviewsProps {
  productTitle?: string;
  productImages?: string[];
  productId?: string;
  productHandle?: string;
}

function StarRating({ rating, size = 16 }: { rating: number; size?: number }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`${i <= rating ? "text-amber-500 fill-amber-500" : "text-muted-foreground/30"}`}
          style={{ width: size, height: size }}
        />
      ))}
    </div>
  );
}

function RatingBar({ stars, count, total }: { stars: number; count: number; total: number }) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="w-8 text-right text-muted-foreground">{stars}★</span>
      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
        <div className="h-full bg-amber-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
      <span className="w-8 text-muted-foreground text-xs">{count}</span>
    </div>
  );
}

function ReviewCard({ review, currentUserId }: { review: Review; currentUserId?: string }) {
  const [helpful, setHelpful] = useState(false);
  const [helpfulCount, setHelpfulCount] = useState(review.helpfulCount || 0);
  const [voting, setVoting] = useState(false);
  const isDbReview = review.id.startsWith("db-");
  const dbId = isDbReview ? review.id.replace("db-", "") : null;

  // Check if user already voted on this DB review
  useEffect(() => {
    if (!currentUserId || !dbId) return;
    getSupabase()
      .then((supabase) => supabase
      .from("review_helpful_votes")
      .select("id")
      .eq("review_id", dbId)
      .eq("user_id", currentUserId)
      .maybeSingle())
      .then(({ data }) => {
        if (data) setHelpful(true);
      });
  }, [currentUserId, dbId]);

  const handleHelpful = async () => {
    // For LAI reviews, just toggle locally
    if (!isDbReview) {
      setHelpful(true);
      setHelpfulCount((c) => c + 1);
      return;
    }

    if (!currentUserId) {
      toast("Please log in to mark reviews as helpful", {
        action: { label: "Log In", onClick: () => (window.location.href = "/auth") },
      });
      return;
    }

    if (voting || !dbId) return;
    setVoting(true);

    try {
      const supabase = await getSupabase();
      if (helpful) {
        // Remove vote
        await supabase
          .from("review_helpful_votes")
          .delete()
          .eq("review_id", dbId)
          .eq("user_id", currentUserId);
        await supabase.rpc("decrement_helpful_count", { review_uuid: dbId });
        setHelpful(false);
        setHelpfulCount((c) => Math.max(c - 1, 0));
      } else {
        // Add vote
        const { error } = await supabase
          .from("review_helpful_votes")
          .insert({ review_id: dbId, user_id: currentUserId });
        if (error) {
          if (error.code === "23505") {
            // Already voted (unique constraint)
            setHelpful(true);
            return;
          }
          throw error;
        }
        await supabase.rpc("increment_helpful_count", { review_uuid: dbId });
        setHelpful(true);
        setHelpfulCount((c) => c + 1);
      }
    } catch (err) {
      console.error("Error toggling helpful:", err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setVoting(false);
    }
  };

  const formattedDate = review.date
    ? new Date(review.date).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
    : "";

  return (
    <div className="border-b border-border py-5 last:border-b-0">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <StarRating rating={review.rating} size={14} />
            {review.verified && (
              <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600 font-medium">
                <CheckCircle2 className="h-3 w-3" /> Verified Purchase
              </span>
            )}
            {review.source === "user" && !review.verified && (
              <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground font-medium">
                <MessageSquare className="h-3 w-3" /> Submitted on loftloom.com
              </span>
            )}
          </div>
          {review.title && (
            <h4 className="text-sm font-semibold text-foreground mb-1">{review.title}</h4>
          )}
          <p className="text-[13px] text-muted-foreground leading-relaxed">{review.body}</p>

          {review.photos.length > 0 && (
            <div className="flex gap-2 mt-3">
              {review.photos.map((photo, i) => (
                <img
                  key={i}
                  src={photo}
                  alt={`Review photo ${i + 1}`}
                  className="w-16 h-16 object-cover rounded border border-border"
                  loading="lazy"
                />
              ))}
            </div>
          )}

          <div className="flex items-center gap-4 mt-3">
            <span className="text-xs text-muted-foreground">
              {review.author}{review.country ? ` · ${review.country}` : ""}{formattedDate ? ` · ${formattedDate}` : ""}
            </span>
            <button
              onClick={handleHelpful}
              disabled={voting}
              className={`inline-flex items-center gap-1 text-xs transition-colors ${
                helpful ? "text-primary" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <ThumbsUp className="h-3 w-3" />
              {helpful ? "Helpful!" : "Helpful"}
              {helpfulCount > 0 && <span className="text-muted-foreground">({helpfulCount})</span>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// NOTE: Reviews shown here are real only — from the store's review data
// (fetch-reviews edge function) and customer-submitted reviews in the DB.
// Never generate synthetic/sample reviews here.


export const ProductReviews = ({ productId, productHandle }: ProductReviewsProps) => {
  const [laiReviews, setLaiReviews] = useState<Review[]>([]);
  const [dbReviews, setDbReviews] = useState<Review[]>([]);
  const [rating, setRating] = useState<number | null>(null);
  const [laiCount, setLaiCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filterRating, setFilterRating] = useState<number | null>(null);
  const [apiDistribution, setApiDistribution] = useState<Record<string, number> | null>(null);
  const [showWriteForm, setShowWriteForm] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [sortBy, setSortBy] = useState<SortOption>("newest");
  const [photosOnly, setPhotosOnly] = useState(false);

  // Check auth state
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let cancelled = false;
    getSupabase().then((supabase) => {
      if (cancelled) return;
      const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
        setCurrentUser(session?.user ?? null);
      });
      unsubscribe = () => subscription.unsubscribe();
      supabase.auth.getUser().then(({ data }) => {
        if (!cancelled) setCurrentUser(data.user);
      });
    });
    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, []);

  // Fetch DB reviews
  const fetchDbReviews = useCallback(async () => {
    if (!productId) return;
    const supabase = await getSupabase();
    const { data, error } = await supabase
      .from("reviews")
      .select("*")
      .eq("product_id", productId)
      .eq("status", "approved")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching DB reviews:", error);
      return;
    }

    const mapped: Review[] = (data || []).map((r: any) => ({
      id: `db-${r.id}`,
      author: r.author_name,
      rating: r.rating,
      title: r.title || "",
      body: r.body,
      date: r.created_at,
      verified: false,
      photos: r.photos || [],
      country: "",
      source: "user" as const,
      helpfulCount: r.helpful_count || 0,
    }));
    setDbReviews(mapped);
  }, [productId]);

  // Fetch LAI reviews
  const fetchLaiReviews = useCallback(async (pageNum: number, append = false) => {
    if (!productId) return;
    if (pageNum === 1) setLoading(true);
    else setLoadingMore(true);

    try {
      const supabase = await getSupabase();
      const { data, error } = await supabase.functions.invoke("fetch-reviews", {
        body: { productId, handle: productHandle, page: pageNum },
      });

      if (error) {
        console.error("Error fetching reviews:", error);
        return;
      }

      if (data) {
        const laiMapped = (data.reviews || []).map((r: Review) => ({ ...r, source: "lai" as const }));
        if (append) {
          setLaiReviews((prev) => [...prev, ...laiMapped]);
        } else {
          setLaiReviews(laiMapped);
        }
        if (data.rating !== null) setRating(data.rating);
        if (data.count) setLaiCount(data.count);
        if (data.distribution) setApiDistribution(data.distribution);
        setHasMore(data.hasMore || false);
        setPage(pageNum);
      }
    } catch (err) {
      console.error("Failed to fetch reviews:", err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [productId, productHandle]);

  useEffect(() => {
    fetchLaiReviews(1);
    fetchDbReviews();
  }, [fetchLaiReviews, fetchDbReviews]);

  const loadMore = () => fetchLaiReviews(page + 1, true);

  // Merge reviews: customer-submitted first, then store reviews. Real data only.
  const allReviews = [...dbReviews, ...laiReviews];
  const totalCount = laiCount + dbReviews.length;

  // Calculate combined rating from real reviews only
  const combinedRating = (() => {
    if (dbReviews.length === 0 && rating !== null) return rating;
    if (dbReviews.length > 0 && rating !== null && laiCount > 0) {
      const laiTotal = rating * laiCount;
      const dbTotal = dbReviews.reduce((sum, r) => sum + r.rating, 0);
      return (laiTotal + dbTotal) / (laiCount + dbReviews.length);
    }
    if (dbReviews.length > 0) {
      return dbReviews.reduce((sum, r) => sum + r.rating, 0) / dbReviews.length;
    }
    return null;
  })();

  const sortedAndFilteredReviews = useMemo(() => {
    let result = filterRating
      ? allReviews.filter((r) => r.rating === filterRating)
      : [...allReviews];

    if (photosOnly) {
      result = result.filter((r) => r.photos.length > 0);
    }

    switch (sortBy) {
      case "newest":
        result.sort((a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime());
        break;
      case "oldest":
        result.sort((a, b) => new Date(a.date || 0).getTime() - new Date(b.date || 0).getTime());
        break;
      case "highest":
        result.sort((a, b) => b.rating - a.rating);
        break;
      case "lowest":
        result.sort((a, b) => a.rating - b.rating);
        break;
      case "helpful":
        result.sort((a, b) => (b.helpfulCount || 0) - (a.helpfulCount || 0));
        break;
    }

    return result;
  }, [allReviews, filterRating, photosOnly, sortBy]);

  // Build distribution combining LAI + DB
  const distribution = [5, 4, 3, 2, 1].map((stars) => {
    const laiPart = apiDistribution ? (apiDistribution[String(stars)] || 0) : 0;
    const dbPart = dbReviews.filter((r) => r.rating === stars).length;
    return { stars, count: laiPart + dbPart };
  });

  const withPhotos = allReviews.filter((r) => r.photos.length > 0);

  const handleWriteClick = () => {
    // No account required — anonymous reviews are accepted and moderated before publishing.
    setShowWriteForm(true);
  };

  const handleReviewSubmitted = () => {
    setShowWriteForm(false);
    fetchDbReviews();
  };

  if (!productId) {
    return null;
  }

  if (loading) {
    return (
      <section className="border-t border-border">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-12 md:py-16 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </section>
    );
  }

  // No reviews yet — invite the first real review instead of hiding the section
  if (allReviews.length === 0 && totalCount === 0) {
    return (
      <section className="border-t border-border">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-12 md:py-16">
          <h2 className="font-sans text-[22px] md:text-[26px] font-semibold text-foreground mb-4">
            Customer Reviews
          </h2>
          {showWriteForm && productHandle ? (
            <WriteReviewForm
              productId={productId}
              productHandle={productHandle}
              onSubmitted={handleReviewSubmitted}
              onCancel={() => setShowWriteForm(false)}
            />
          ) : (
            <div className="border border-border p-6 md:p-8 max-w-3xl">
              {/* No reviews, so nothing that reads like one: the buy box above
                  already carries delivery, returns and damage cover. */}
              <p className="text-sm text-muted-foreground mb-6 max-w-xl">
                No one has reviewed this piece yet.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleWriteClick}
                  className="px-6 py-2.5 bg-foreground text-background text-[13px] font-semibold uppercase tracking-widest hover:opacity-90 transition-opacity"
                >
                  Be the first to review
                </button>
                <a
                  href="#product-questions"
                  className="px-6 py-2.5 border border-foreground text-foreground text-[13px] font-semibold uppercase tracking-widest text-center hover:bg-foreground hover:text-background transition-colors"
                >
                  Ask a question
                </a>
              </div>
            </div>
          )}
        </div>
      </section>
    );
  }

  // Has reviews
  return (
    <section className="border-t border-border">
      <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-12 md:py-16">
        <h2 className="font-sans text-[22px] md:text-[26px] font-semibold text-foreground mb-8">
          Customer Reviews
        </h2>

        {showWriteForm && productHandle ? (
          <div className="mb-8">
            <WriteReviewForm
              productId={productId}
              productHandle={productHandle}
              onSubmitted={handleReviewSubmitted}
              onCancel={() => setShowWriteForm(false)}
            />
          </div>
        ) : null}

        <div className="grid md:grid-cols-[280px,1fr] gap-8">
          {/* Summary sidebar */}
          <div>
            <div className="text-center md:text-left mb-6">
              <div className="text-4xl font-bold text-foreground">
                {combinedRating !== null ? combinedRating.toFixed(1) : "—"}
              </div>
              <StarRating rating={Math.round(combinedRating || 0)} size={18} />
              <p className="text-sm text-muted-foreground mt-1">
                Based on {totalCount} review{totalCount !== 1 ? "s" : ""}
              </p>
            </div>

            {/* Rating distribution */}
            <div className="space-y-1.5 mb-6">
              {distribution.map(({ stars, count: c }) => (
                <button
                  key={stars}
                  onClick={() => setFilterRating(filterRating === stars ? null : stars)}
                  className={`w-full ${filterRating === stars ? "opacity-100" : "opacity-70 hover:opacity-100"} transition-opacity`}
                >
                  <RatingBar stars={stars} count={c} total={totalCount} />
                </button>
              ))}
            </div>

            {filterRating && (
              <button
                onClick={() => setFilterRating(null)}
                className="text-xs text-primary hover:underline mb-4"
              >
                Clear filter — show all
              </button>
            )}

            {/* Photo reviews */}
            {withPhotos.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center gap-1.5 text-sm font-medium text-foreground mb-2">
                  <Camera className="h-4 w-4" /> Photos ({withPhotos.length})
                </div>
                <div className="flex gap-1.5 flex-wrap">
                  {withPhotos.slice(0, 6).flatMap((r) =>
                    r.photos.slice(0, 2).map((photo, i) => (
                      <img
                        key={`${r.id}-${i}`}
                        src={photo}
                        alt="Customer photo"
                        className="w-14 h-14 object-cover rounded border border-border"
                        loading="lazy"
                      />
                    ))
                  )}
                </div>
              </div>
            )}

            <button
              onClick={handleWriteClick}
              className="w-full px-5 py-2.5 border border-border text-[13px] font-semibold uppercase tracking-widest hover:border-foreground/40 transition-colors"
            >
              Write a Review
            </button>
          </div>

          {/* Reviews list */}
          <div>
            {/* Sort & filter controls */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <div className="flex items-center gap-1.5">
                <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground" />
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as SortOption)}
                  className="text-[13px] bg-background border border-border rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="highest">Highest Rated</option>
                  <option value="lowest">Lowest Rated</option>
                  <option value="helpful">Most Helpful</option>
                </select>
              </div>

              <button
                onClick={() => setPhotosOnly(!photosOnly)}
                className={`inline-flex items-center gap-1.5 text-[12px] tracking-wide uppercase font-medium px-3 py-1.5 rounded-full border transition-colors ${
                  photosOnly
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border text-muted-foreground hover:border-foreground/30"
                }`}
              >
                <ImageIcon className="h-3.5 w-3.5" />
                With Photos
              </button>
            </div>

            <p className="text-sm text-muted-foreground mb-4">
              Showing {sortedAndFilteredReviews.length} review{sortedAndFilteredReviews.length !== 1 ? "s" : ""}
              {filterRating ? ` with ${filterRating} star${filterRating !== 1 ? "s" : ""}` : ""}
              {photosOnly ? " with photos" : ""}
            </p>
            <div>
              {sortedAndFilteredReviews.map((review) => (
                <ReviewCard key={review.id} review={review} currentUserId={currentUser?.id} />
              ))}
              {sortedAndFilteredReviews.length === 0 && (
                <p className="text-sm text-muted-foreground py-8 text-center">
                  No reviews match your filters.
                </p>
              )}
            </div>

            {hasMore && !filterRating && (
              <div className="flex justify-center mt-6">
                <button
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="inline-flex items-center gap-2 px-6 py-2.5 border border-border text-[13px] font-medium hover:border-foreground/40 transition-colors rounded-full"
                >
                  {loadingMore ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Loading...</>
                  ) : (
                    "Load More Reviews"
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
